import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * AutocompleteInput - Input with dropdown suggestions
 * Shows a dropdown with filtered suggestions as the user types
 */
const AutocompleteInput = ({
  value,
  onChange,
  suggestions = [],
  placeholder = "",
  className = "",
  maxSuggestions = 10,
  minCharsToShow = 1,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filter suggestions based on input value
  useEffect(() => {
    if (suggestions && suggestions.length > 0) {
      if (value && value.length >= minCharsToShow) {
        const searchTerm = value.toLowerCase();
        const filtered = suggestions
          .filter(s => s && s.toLowerCase().includes(searchTerm))
          .slice(0, maxSuggestions);
        setFilteredSuggestions(filtered);
        setIsOpen(filtered.length > 0);
      } else if (value === "" || value === undefined) {
        // Show first suggestions when input is empty but focused
        setFilteredSuggestions(suggestions.slice(0, maxSuggestions));
      } else {
        setFilteredSuggestions([]);
        setIsOpen(false);
      }
    }
  }, [value, suggestions, maxSuggestions, minCharsToShow]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (suggestion) => {
    onChange({ target: { value: suggestion } });
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    onChange(e);
  };

  const handleFocus = () => {
    // Show suggestions when focusing, even if empty
    if (filteredSuggestions.length > 0) {
      setIsOpen(true);
    } else if (suggestions && suggestions.length > 0) {
      // Show initial suggestions on focus
      setFilteredSuggestions(suggestions.slice(0, maxSuggestions));
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "ArrowDown" && isOpen && filteredSuggestions.length > 0) {
      e.preventDefault();
      // Could implement keyboard navigation here
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        {...props}
      />
      
      {isOpen && filteredSuggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] max-w-[300px] bg-white border border-slate-200 rounded-md shadow-lg">
          <ScrollArea className="max-h-[200px]">
            <div className="py-1">
              {filteredSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="w-full px-3 py-2 text-left text-xs hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 focus:outline-none transition-colors cursor-pointer"
                  onClick={() => handleSelect(suggestion)}
                >
                  {/* Highlight matching text */}
                  {highlightMatch(suggestion, value)}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

// Helper to highlight matching text
const highlightMatch = (text, search) => {
  if (!search) return text;
  
  const searchLower = search.toLowerCase();
  const textLower = text.toLowerCase();
  const index = textLower.indexOf(searchLower);
  
  if (index === -1) return text;
  
  return (
    <>
      {text.substring(0, index)}
      <span className="font-semibold text-blue-600 bg-blue-100 rounded px-0.5">
        {text.substring(index, index + search.length)}
      </span>
      {text.substring(index + search.length)}
    </>
  );
};

export default AutocompleteInput;
